import { Box, Divider, HStack } from "@chakra-ui/react";
import { DataTable, NavBar } from "@components/ui-kit";
import { Pagination } from "@components/ui-kit/table/pagination";
import { Trans, t } from "@lingui/macro";
import { BrandCreditAccountsWhereColumn } from "@src/__generated__/urql-graphql";
import { CreditAccountsTypeLinks } from "@src/components/modules/credit-account/global-accounts-listing/components/credit-accounts-type-links";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import cx from "classnames";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import { type FC, type ReactElement, useContext } from "react";
import { ModalCreateCreditAccount } from "../form/ModalCreateCreditAccount";
import { BoxCreditAccountsSummary } from "./BoxCreditAccountsSummary";
import s from "./CardListCreditAccounts.module.css";
import { PageCreditAccountsCtx } from "./PageCreditAccounts";
import { QuickFilters } from "./StoreCreditAccounts";
import { TableCreditAccounts } from "./table/TableCreditAccounts";

export const CardListCreditAccounts: FC<{
  clientId?: number;
  brandId?: number;
  isSummaryLoaded: boolean;
  navigation?: ReactElement;
}> = observer(function CardListCreditAccounts({
  clientId,
  brandId,
  isSummaryLoaded,
  navigation,
}) {
  const { pathname } = useRouter();
  const store = useContext(PageCreditAccountsCtx);

  const state = useLocalObservable(() => ({
    get statusFilter() {
      return store.where.filters.find(
        (f) => f.column === BrandCreditAccountsWhereColumn.Status,
      );
    },
  }));

  return (
    <div data-testid="card-list-projects">
      <DataTable>
        <HStack justifyContent="space-between" px="4">
          <NavBar fullWidth>
            {navigation ? (
              navigation
            ) : (
              <>
                <CreditAccountsTypeLinks />
                {pathname === "/invoices/credit-accounts" && (
                  <>
                    <Box h="full" py="3">
                      <Divider orientation="vertical" />
                    </Box>
                    <NavBar.Item
                      active={
                        store.activeQuickFilter?.value?.[0] === QuickFilters.All
                      }
                      onClick={() => {
                        state.statusFilter?.clear();
                        store.activeQuickFilter?.setValue(QuickFilters.All);
                      }}
                    >
                      <Trans>All</Trans>
                    </NavBar.Item>
                    <NavBar.Item
                      active={
                        store.activeQuickFilter?.value?.[0] ===
                        QuickFilters.Active
                      }
                      onClick={() => {
                        state.statusFilter?.setValue(QuickFilters.Active);
                        store.activeQuickFilter?.setValue(QuickFilters.Active);
                      }}
                    >
                      <Trans>Active</Trans>
                    </NavBar.Item>
                    <NavBar.Item
                      active={
                        store.activeQuickFilter?.value?.[0] ===
                        QuickFilters.Archived
                      }
                      onClick={() => {
                        state.statusFilter?.setValue(QuickFilters.Archived);
                        store.activeQuickFilter?.setValue(
                          QuickFilters.Archived,
                        );
                      }}
                    >
                      <Trans>Archived</Trans>
                    </NavBar.Item>
                  </>
                )}
              </>
            )}
          </NavBar>
          <HStack>
            <ModalCreateCreditAccount clientId={clientId} brandId={brandId} />
          </HStack>
        </HStack>
        <hr />
        <div data-testid="filters" className={cx(s.filters, "p-6")}>
          <FiltersPanel<BrandCreditAccountsWhereColumn>
            filters={store.where}
            searchPlaceholder={t`Search by title, brand`}
            onSearch={(val) => {
              store.searchTerm = val;
              store.pagination.resetPage();
            }}
          />
        </div>

        <Box mb="4">
          <BoxCreditAccountsSummary
            isLoaded={isSummaryLoaded}
            projectsCount={store.pagination.total ?? 0}
            summary={store.brandCreditAccountsSummary}
          />
        </Box>

        <div data-testid="table">
          <TableCreditAccounts />
          <Pagination store={store.pagination} />
        </div>
      </DataTable>
    </div>
  );
});
